import React, { createContext, useContext, useState, ReactNode } from 'react';
import { initialTwilioChatState, initialTwilioSettingsState, initialTwilioTimerState } from '../utils/chats';

interface ChatGlobalContextType {
  webchatData: any;
  appMessages: any;
  twilioChat: any;
  twilioTimer: any;
  twilioSettings: any;
  showAgentBanner: boolean;
  setShowAgentBanner: (data: boolean) => void;
  showBusinessModal: boolean;
  setShowBusinessModal: (data: boolean) => void;
  goGetTwilioChat: () => any;
  goSetTwilioChat: (twilioChat: any) => void;
  goResetTwilioChat: () => void;
  goSetTwilioTimer: (twilioTimer: any) => void;
  goResetTwilioTimer: () => void;
  goSetTwilioSettings: (twilioSettings: any) => void;
  goResetTwilioSettings: () => void;
  goSetWebchatData: (data: any) => void;
  goResetWebchatData: () => void;
  goSetRegistrationData: (registrationData: any) => void;
  goResetRegistrationData: () => void;
  goGetRegistrationData: () => any;
  setAppMessages: (data: any) => void;
}

const ChatGlobalContext = createContext<ChatGlobalContextType>({} as ChatGlobalContextType);

interface ChatGlobalProviderProps {
  children: ReactNode;
}

export const ChatGlobalProvider: React.FC<ChatGlobalProviderProps> = ({ children }) => {
  const [webchatData, setWebchatData] = useState<any>({});
  const [appMessages, setAppMessages] = useState<any>({});
  const [registrationData, setRegistrationData] = useState(null);
  const [twilioChat, setTwilioChat] = useState<any>({});
  const [twilioTimer, setTwilioTimer] = useState({});
  const [twilioSettings, setTwilioSettings] = useState({});
  const [showAgentBanner, setShowAgentBanner] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  

  const goSetWebchatData = (data: any) => {
    setWebchatData(data);
    if (typeof window !== 'undefined') {
      localStorage.setItem('webchatData', JSON.stringify(data));
    }
  };

  const goResetWebchatData = () => {
    goSetWebchatData(null);
  };

  const goSetTwilioChat = (twilioChat: any) => {
    if (twilioChat) setTwilioChat(twilioChat);
    if (typeof window !== 'undefined' && window) {
      // We don't want to have "messages" in Local Storage
      const { messages, ...NewTwilioChat } = twilioChat || {};
      localStorage.setItem('twilioChat', JSON.stringify(NewTwilioChat));
    }
  };

  const goGetTwilioChat = () => {
    if (typeof window !== 'undefined' && window) {
      return {...JSON.parse(localStorage.getItem('twilioChat') || JSON.stringify(initialTwilioChatState)), messages: twilioChat.messages || []};
    }
  }
  
  const goResetTwilioChat = () => {
    if (typeof window !== 'undefined' && window) {
      const tmpTwilioChat = {...initialTwilioChatState,
        openChat: twilioChat.openChat,
        fullscreen: twilioChat.fullscreen,
        side: twilioChat.side
      };
      setTwilioChat(tmpTwilioChat);
      const { messages, ...NewTwilioChat } = tmpTwilioChat;
      localStorage.setItem('twilioChat', JSON.stringify(NewTwilioChat));
    }
  }
  
  const goSetTwilioTimer = (twilioTimer) => {
    if (typeof window !== 'undefined' && twilioTimer){
      setTwilioTimer(twilioTimer);
      localStorage.setItem('twilioTimer', JSON.stringify(twilioTimer));
    }
  }  
  
  const goResetTwilioTimer = () => {
    if (typeof window !== 'undefined' && window) {
      const tmpTwilioTimer = {...initialTwilioTimerState,
        chatUnreaded: false,
        lastTimeActive: null
      };
      localStorage.removeItem("timer");
      setTwilioTimer(tmpTwilioTimer);
      localStorage.setItem('twilioTimer', JSON.stringify(tmpTwilioTimer));
    }
  }
  
  const goSetTwilioSettings = (twilioSettings) => {
    if(typeof window !== 'undefined' && twilioSettings){
      setTwilioSettings(twilioSettings);
      localStorage.setItem('twilioSettings', JSON.stringify(twilioSettings));
    }
  }  
  
  const goResetTwilioSettings = () => {
    if (typeof window !== 'undefined' && window) {
      const tmpTwilioSettings = {
        ...initialTwilioSettingsState,
        fullscreen: false,
        side: 'right'
      };
      setTwilioSettings(tmpTwilioSettings);
      localStorage.setItem('twilioSettings', JSON.stringify(tmpTwilioSettings));
    }
  }

  return (
    <ChatGlobalContext.Provider
      value={{
        webchatData,
        appMessages,
        twilioChat,
        twilioTimer,
        setAppMessages,
        goGetTwilioChat,
        goSetTwilioChat,
        goResetTwilioChat,
        goSetTwilioTimer,
        goResetTwilioTimer,
        goSetTwilioSettings,
        goResetTwilioSettings,
        twilioSettings,
        showAgentBanner,
        setShowAgentBanner,
        showBusinessModal,
        setShowBusinessModal,
        goSetWebchatData,
        goResetWebchatData,
        goSetRegistrationData: setRegistrationData,
        goResetRegistrationData: () => setRegistrationData(null),
        goGetRegistrationData: () => registrationData
      }}
    >
      {children}
    </ChatGlobalContext.Provider>
  );
};

export default ChatGlobalContext;
